
import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@/src/contexts/AuthContext';
import { OptimizeBusiness, BusinessIdea } from '@/src/types';
import { db, collection, getDocs, doc, setDoc, handleFirestoreError, OperationType } from '../firebase';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';

interface Props {
  onClose: () => void;
  savedVentures: BusinessIdea[];
}

const OptimizeDashboard: React.FC<Props> = ({ onClose, savedVentures }) => {
  const { user, userProfile, isDemoGuest } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'growth' | 'strategy' | 'ops' | 'swot' | 'exit'>('overview');
  const [selectedBusiness, setSelectedBusiness] = useState<OptimizeBusiness | null>(null);
  const [businesses, setBusinesses] = useState<OptimizeBusiness[]>([]);
  const [loading, setLoading] = useState(true);

  const renderKeys = new Set<string>();
  const validateAndGetUniqueKey = (id: string | number, idx: number, prefix: string): string => {
    let key = `${prefix}-${id}-${idx}`;
    if (renderKeys.has(key)) {
      console.warn(`[Key Validation] Duplicate key detected in OptimizeDashboard: ${key}. Resolving...`);
      let suffix = 1;
      while (renderKeys.has(`${key}-${suffix}`)) {
        suffix++;
      }
      key = `${key}-${suffix}`;
    }
    renderKeys.add(key);
    return key;
  };
  const [isMigrating, setIsMigrating] = useState(false);

  useEffect(() => {
    const fetchBusinesses = async () => {
      if (!user) return;
      if (isDemoGuest) {
        try {
          const saved = localStorage.getItem('demo_guest_optimize_businesses');
          const bizData = saved ? JSON.parse(saved) : [];
          setBusinesses(bizData);
          if (bizData.length > 0) {
            setSelectedBusiness(bizData[0]);
          }
        } catch (err) {
          console.warn('Error reading demo guest businesses:', err);
          setBusinesses([]);
        } finally {
          setLoading(false);
        }
        return;
      }
      try {
        const querySnapshot = await getDocs(collection(db, `users/${user.uid}/optimize/businesses`));
        const bizData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OptimizeBusiness));
        setBusinesses(bizData);
        if (bizData.length > 0) {
          setSelectedBusiness(bizData[0]);
        }
      } catch (error) {
        handleFirestoreError(error, OperationType.LIST, `users/${user.uid}/optimize/businesses`);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, [user, isDemoGuest]);

  const handleMigrate = async (venture: BusinessIdea) => {
    if (!user || !venture.id) return;
    setIsMigrating(true);
    
    const newBiz: OptimizeBusiness = {
      id: venture.id, // Deterministic key from original venture
      sourceVentureId: venture.id,
      title: venture.title,
      category: venture.category,
      location: venture.locationName || 'Unknown',
      connectionStatus: 'connected',
      performanceData: {
        revenue: [
          { period: 'Jan', projected: 5000, actual: 4800 },
          { period: 'Feb', projected: 5500, actual: 5200 },
          { period: 'Mar', projected: 6000, actual: 6400 },
          { period: 'Apr', projected: 6500, actual: 6100 },
          { period: 'May', projected: 7000, actual: 7800 },
          { period: 'Jun', projected: 7500, actual: 8200 },
        ],
        expenses: [
          { period: 'Jan', projected: 3000, actual: 3100 },
          { period: 'Feb', projected: 3000, actual: 2900 },
          { period: 'Mar', projected: 3200, actual: 3300 },
          { period: 'Apr', projected: 3200, actual: 3100 },
          { period: 'May', projected: 3500, actual: 3400 },
          { period: 'Jun', projected: 3500, actual: 3600 },
        ],
        customers: [
          { period: 'Jan', projected: 100, actual: 95 },
          { period: 'Feb', projected: 120, actual: 115 },
          { period: 'Mar', projected: 140, actual: 150 },
          { period: 'Apr', projected: 160, actual: 155 },
          { period: 'May', projected: 180, actual: 200 },
          { period: 'Jun', projected: 200, actual: 220 },
        ]
      },
      liveSwot: {
        strengths: ['High customer retention', 'Optimized supply chain'],
        weaknesses: ['Limited marketing reach', 'High initial capex'],
        opportunities: ['Expansion to neighboring district', 'New product line launch'],
        threats: ['Rising labor costs', 'New competitor entry']
      },
      historicalFeasibilitySnapshot: venture,
      timestamp: Date.now()
    };

    try {
      if (isDemoGuest) {
        const updated = [...businesses, newBiz];
        localStorage.setItem('demo_guest_optimize_businesses', JSON.stringify(updated));
        setBusinesses(updated);
        setSelectedBusiness(newBiz);
      } else {
        await setDoc(doc(db, `users/${user.uid}/optimize/businesses`, newBiz.id), newBiz);
        setBusinesses(prev => [...prev, newBiz]);
        setSelectedBusiness(newBiz);
      }
    } catch (error) {
      if (!isDemoGuest) {
        handleFirestoreError(error, OperationType.WRITE, `users/${user.uid}/optimize/businesses/${newBiz.id}`);
      }
    } finally {
      setIsMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[200] bg-[#322D29] flex flex-col items-center justify-center space-y-8">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 border-3 border-[#F26522]/20 rounded-full"></div>
          <div className="absolute inset-0 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="fas fa-gear text-[#F26522] text-xl animate-pulse"></i>
          </div>
        </div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/60 font-heading">Synchronizing Enterprise Data...</p>
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="fixed inset-0 z-[200] bg-[#322D29] flex flex-col animate-slide-up overflow-hidden">
        <header className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between bg-[#3a3430]">
          <button onClick={onClose} className="flex items-center gap-2 text-white/60 hover:text-white transition-all group cursor-pointer">
            <i className="fas fa-chevron-left text-xs group-hover:-translate-x-1"></i>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] font-heading">Exit Hub</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-[#F26522] text-white flex items-center justify-center font-bold text-xs">
              <i className="fas fa-gauge-high"></i>
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-white font-heading">eNvesta Optimize</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8 max-w-4xl mx-auto w-full space-y-12 custom-scrollbar">
          <div className="text-center space-y-3">
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-white font-heading">Connect Your Business</h1>
            <p className="text-sm text-white/60 leading-relaxed max-w-lg mx-auto font-normal">
              Select an existing feasibility study to migrate or connect a new business to start scaling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6 flex flex-col">
              <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#F26522] font-heading">Scale Existing Study</h3>
                <p className="text-lg font-medium text-white leading-snug">Migrate a Launchpad or Discovery venture into active operations.</p>
              </div>
              
              <div className="flex-1 space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                {savedVentures.length > 0 ? (
                  savedVentures.map((venture, i) => {
                    const uniqueVentureKey = validateAndGetUniqueKey(venture.id || 'venture', i, 'migrate-venture');
                    return (
                    <div key={uniqueVentureKey} className="p-4 bg-[#322D29] border border-white/[0.06] rounded-lg flex items-center justify-between group hover:border-[#F26522]/50 transition-all">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-white">{venture.title}</p>
                        <p className="text-[9px] text-white/50 uppercase tracking-wider">{venture.category} • {venture.locationName}</p>
                      </div>
                      <button 
                        onClick={() => handleMigrate(venture)}
                        disabled={isMigrating}
                        className="px-3 py-1.5 bg-[#F26522] text-white text-[9px] font-bold uppercase tracking-wider rounded hover:bg-[#F26522]/90 transition-all disabled:opacity-50 cursor-pointer font-heading"
                      >
                        {isMigrating ? 'Migrating...' : 'Scale This'}
                      </button>
                    </div>
                  );
                  })
                ) : (
                  <div className="py-10 text-center space-y-3">
                    <i className="fas fa-folder-open text-white/20 text-3xl"></i>
                    <p className="text-xs text-white/40 uppercase tracking-wider">No saved ventures found.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6 flex flex-col">
              <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#F26522] font-heading">Connect New Entity</h3>
                <p className="text-lg font-medium text-white leading-snug">Integrate live accounting data or enter business metrics manually.</p>
              </div>
              
              <div className="flex-1 space-y-4 flex flex-col justify-center">
                <div className="grid grid-cols-2 gap-3">
                  <button className="p-5 bg-[#322D29] border border-white/[0.06] hover:border-[#F26522]/60 rounded-lg transition-all flex flex-col items-center gap-2 cursor-pointer">
                    <i className="fas fa-file-invoice-dollar text-xl text-[#F26522]"></i>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">QuickBooks</span>
                  </button>
                  <button className="p-5 bg-[#322D29] border border-white/[0.06] hover:border-[#F26522]/60 rounded-lg transition-all flex flex-col items-center gap-2 cursor-pointer">
                    <i className="fas fa-calculator text-xl text-[#F26522]"></i>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">Manual Entry</span>
                  </button>
                </div>
                <button className="w-full py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-medium uppercase text-xs tracking-wider rounded-lg transition-all cursor-pointer">
                  Request API Integration
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] bg-[#322D29] flex flex-col animate-slide-up overflow-hidden">
      <header className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between bg-[#3a3430]">
        <div className="flex items-center gap-6">
          <button onClick={onClose} className="flex items-center gap-2 text-white/60 hover:text-white transition-all group cursor-pointer">
            <i className="fas fa-chevron-left text-xs group-hover:-translate-x-1"></i>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] font-heading">Exit Hub</span>
          </button>
          
          <div className="h-4 w-px bg-white/10"></div>
          
          <div className="flex items-center gap-4">
            <select 
              value={selectedBusiness?.id}
              onChange={(e) => setSelectedBusiness(businesses.find(b => b.id === e.target.value) || null)}
              className="bg-[#322D29] border border-white/[0.08] text-white px-3 py-1.5 rounded-lg text-xs font-medium uppercase tracking-wider outline-none focus:border-[#F26522] transition-colors cursor-pointer"
            >
              {businesses.map((b, idx) => {
                const uniqueKey = validateAndGetUniqueKey(b.id, idx, 'biz-option');
                return (
                  <option key={uniqueKey} value={b.id} className="bg-[#322D29] text-white">{b.title}</option>
                );
              })}
            </select>
            <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
              Live Connection
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-[#F26522] text-white flex items-center justify-center font-bold text-xs">
              <i className="fas fa-gauge-high"></i>
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-white font-heading">Optimize Dashboard</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Navigation */}
        <aside className="w-64 border-r border-white/[0.06] bg-[#322D29] flex flex-col shrink-0">
          <nav className="flex-1 p-3 space-y-1">
            {[
              { id: 'overview', label: 'Health Check', icon: 'fa-heart-pulse' },
              { id: 'growth', label: 'Growth Math', icon: 'fa-arrow-up-right-dots' },
              { id: 'strategy', label: 'Strategy Sandbox', icon: 'fa-chess-knight' },
              { id: 'ops', label: 'Live Ops', icon: 'fa-tower-broadcast' },
              { id: 'swot', label: 'Living SWOT', icon: 'fa-arrows-split-up-and-left' },
              { id: 'exit', label: 'Exit Architect', icon: 'fa-door-open' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-xs font-medium uppercase tracking-wider transition-all cursor-pointer ${
                  activeTab === tab.id 
                    ? 'bg-[#3a3430] text-white border border-white/[0.08]' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
              >
                <i className={`fas ${tab.icon} w-4 text-xs ${activeTab === tab.id ? 'text-[#F26522]' : 'text-white/40'}`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
          
          <div className="p-4 border-t border-white/[0.06] bg-[#3a3430] space-y-3">
            <div className="space-y-0.5">
              <p className="text-[8px] font-bold uppercase tracking-widest text-white/40">Subscription Tier</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#F26522]">Optimize Pro</p>
            </div>
            <button className="w-full py-2 bg-[#322D29] hover:bg-white/5 text-white/80 hover:text-white border border-white/[0.06] rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all cursor-pointer">
              Manage Billing
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-8 lg:p-12 bg-[#322D29] custom-scrollbar">
          <div className="max-w-5xl mx-auto space-y-8">
            {activeTab === 'overview' && (
              <div className="space-y-8 animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">Health Score</p>
                    <div className="flex items-end gap-1.5">
                      <span className="text-4xl font-medium tracking-tight text-white">84</span>
                      <span className="text-lg font-bold text-emerald-400 mb-0.5">/100</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full" style={{ width: '84%' }}></div>
                    </div>
                    <p className="text-xs text-white/60 font-normal">Performing +12% above market baseline.</p>
                  </div>
                  
                  <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">Cash Runway</p>
                    <div className="flex items-end gap-1.5">
                      <span className="text-4xl font-medium tracking-tight text-white">14.2</span>
                      <span className="text-base font-medium text-white/50 mb-0.5">Months</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[#F26522] rounded-full" style={{ width: '60%' }}></div>
                    </div>
                    <p className="text-xs text-white/60 font-normal">Based on current burn rate of $12k/mo.</p>
                  </div>

                  <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">Prediction Accuracy</p>
                    <div className="flex items-end gap-1.5">
                      <span className="text-4xl font-medium tracking-tight text-white">92</span>
                      <span className="text-lg font-bold text-emerald-400 mb-0.5">%</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                    <p className="text-xs text-white/60 font-normal">Actuals closely tracking AI feasibility study.</p>
                  </div>
                </div>

                <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white font-heading">Performance Benchmark</h3>
                      <p className="text-[10px] text-white/50 uppercase tracking-widest">Plan vs. Actual Revenue</p>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded bg-[#F26522]"></span>
                        <span className="text-[9px] font-medium uppercase tracking-wider text-white/60">Projected</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded bg-white"></span>
                        <span className="text-[9px] font-medium uppercase tracking-wider text-white/60">Actual</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={selectedBusiness?.performanceData.revenue}>
                        <defs>
                          <linearGradient id="colorProj" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F26522" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#F26522" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorAct" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#FFFFFF" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.5)' }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.5)' }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#322D29', borderColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                          itemStyle={{ fontSize: '11px', color: '#fff' }}
                        />
                        <Area type="monotone" dataKey="projected" stroke="#F26522" strokeWidth={2} fillOpacity={1} fill="url(#colorProj)" />
                        <Area type="monotone" dataKey="actual" stroke="#FFFFFF" strokeWidth={2} fillOpacity={1} fill="url(#colorAct)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'growth' && (
              <div className="space-y-8 animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-white font-heading">Expansion Capex Modeling</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-4 bg-[#322D29] border border-white/[0.06] rounded-lg">
                        <span className="text-xs text-white/60">New Location Setup</span>
                        <span className="text-sm font-medium text-white">$125,000</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-[#322D29] border border-white/[0.06] rounded-lg">
                        <span className="text-xs text-white/60">Equipment Upgrades</span>
                        <span className="text-sm font-medium text-white">$45,000</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-[#322D29] border border-white/[0.06] rounded-lg">
                        <span className="text-xs text-white/60">Initial Marketing</span>
                        <span className="text-sm font-medium text-white">$20,000</span>
                      </div>
                    </div>
                    <button className="w-full py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-xs font-medium uppercase tracking-wider rounded-lg transition-all cursor-pointer">Calculate ROI</button>
                  </div>

                  <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#F26522] font-heading">Franchise Scenario</h4>
                    <p className="text-xs font-normal text-white/70 leading-relaxed">Based on your current operational efficiency (92%), your model is highly suitable for franchising. AI projects an <span className="text-[#F26522] font-semibold">18% royalty yield</span> per unit.</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest text-white/50">
                        <span>Scalability Index</span>
                        <span className="text-white">High (8.8/10)</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-[#F26522] rounded-full" style={{ width: '88%' }}></div>
                      </div>
                    </div>
                    <button className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-medium uppercase tracking-wider rounded-lg transition-all cursor-pointer">Draft Franchise Disclosure</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'swot' && (
              <div className="space-y-8 animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <div className="p-6 bg-[#3a3430] border border-emerald-500/20 rounded-xl space-y-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 font-heading">Strengths</h4>
                      <ul className="space-y-2.5">
                        {selectedBusiness?.liveSwot.strengths.map((s, i) => {
                          const keyStrength = validateAndGetUniqueKey(s, i, 'strength');
                          return (
                          <li key={keyStrength} className="text-xs text-white/80 flex items-center gap-2.5">
                            <i className="fas fa-plus-circle text-emerald-400 text-[10px]"></i>
                            {s}
                          </li>
                        );
                        })}
                      </ul>
                    </div>
                    <div className="p-6 bg-[#3a3430] border border-blue-500/20 rounded-xl space-y-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-blue-400 font-heading">Opportunities</h4>
                      <ul className="space-y-2.5">
                        {selectedBusiness?.liveSwot.opportunities.map((o, i) => {
                          const keyOpportunity = validateAndGetUniqueKey(o, i, 'opportunity');
                          return (
                          <li key={keyOpportunity} className="text-xs text-white/80 flex items-center gap-2.5">
                            <i className="fas fa-arrow-trend-up text-blue-400 text-[10px]"></i>
                            {o}
                          </li>
                        );
                        })}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="p-6 bg-[#3a3430] border border-[#F26522]/20 rounded-xl space-y-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-[#F26522] font-heading">Weaknesses</h4>
                      <ul className="space-y-2.5">
                        {selectedBusiness?.liveSwot.weaknesses.map((w, i) => {
                          const keyWeakness = validateAndGetUniqueKey(w, i, 'weakness');
                          return (
                          <li key={keyWeakness} className="text-xs text-white/80 flex items-center gap-2.5">
                            <i className="fas fa-minus-circle text-[#F26522] text-[10px]"></i>
                            {w}
                          </li>
                        );
                        })}
                      </ul>
                    </div>
                    <div className="p-6 bg-[#3a3430] border border-rose-500/20 rounded-xl space-y-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-rose-400 font-heading">Threats</h4>
                      <ul className="space-y-2.5">
                        {selectedBusiness?.liveSwot.threats.map((t, i) => {
                          const keyThreat = validateAndGetUniqueKey(t, i, 'threat');
                          return (
                          <li key={keyThreat} className="text-xs text-white/80 flex items-center gap-2.5">
                            <i className="fas fa-triangle-exclamation text-rose-400 text-[10px]"></i>
                            {t}
                          </li>
                        );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl text-center space-y-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#F26522] font-heading">AI Strategy Refresh</p>
                  <p className="text-sm text-white/80 max-w-2xl mx-auto italic font-normal leading-relaxed">"Your Living SWOT has detected a new competitor entry in the 2-mile radius. I recommend accelerating the 'New product line launch' opportunity to maintain market share."</p>
                  <button className="px-8 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-xs font-medium uppercase tracking-wider rounded-lg transition-all cursor-pointer">Update Strategy</button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default OptimizeDashboard;
